import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../../hooks/useCart';
import CartButton from '../../../components/feature/CartButton';
import AddToCartToast from '../../../components/feature/AddToCartToast';

export default function GiftSetPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedSet, setSelectedSet] = useState('premium');
  const [showToast, setShowToast] = useState(false);
  const { addToCart } = useCart();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    deliveryDate: '',
    message: '',
    giftCard: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] =
    useState<'idle' | 'success' | 'error'>('idle');

  // 페이지 진입 시 상단으로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* --------------------------------------------------------------
   * Scroll listener – added with cleanup to avoid memory leaks.
   * -------------------------------------------------------------- */
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const giftSets = [
    {
      id: 'basic',
      name: '기본 세트',
      price: 35000,
      items: '약과 10개 + 유과 150g',
    },
    {
      id: 'premium',
      name: '프리미엄 세트',
      price: 55000,
      items: '약과 20개 + 유과 300g',
    },
    {
      id: 'deluxe',
      name: '디럭스 세트',
      price: 85000,
      items: '약과 30개 + 유과 500g + 특선 한과',
    },
  ];

  const selectedPrice =
    giftSets.find((set) => set.id === selectedSet)?.price || 35000;
  const totalPrice = selectedPrice * quantity;

  const handleQuantityChange = (type: 'increase' | 'decrease') => {
    if (type === 'increase') {
      setQuantity((prev) => prev + 1);
    } else if (type === 'decrease' && quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const selectedSetInfo = giftSets.find((set) => set.id === selectedSet);

      const formBody = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        deliveryDate: formData.deliveryDate,
        message: formData.message,
        giftCard: formData.giftCard ? '예' : '아니오',
        selectedSet: selectedSetInfo?.name || '',
        setItems: selectedSetInfo?.items || '',
        quantity: quantity.toString(),
        totalPrice: totalPrice.toLocaleString() + '원',
      }).toString();

      const response = await fetch(
        'https://readdy.ai/api/form/d60aa70iku00ff9vl1o0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody,
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          address: '',
          deliveryDate: '',
          message: '',
          giftCard: false,
        });
        setQuantity(1);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddToCart = () => {
    const selectedSetInfo = giftSets.find((set) => set.id === selectedSet);
    addToCart({
      id: 'gift-set',
      name: '한과 선물 세트',
      option: selectedSetInfo?.name || '',
      price: selectedSetInfo?.price || 35000,
      quantity,
      image: 'https://public.readdy.ai/ai/img_res/fee385927565bb49fe8d203c377408a1.jpg',
    });
    setShowToast(true);
    setQuantity(1);
  };

  const relatedProducts = [
    {
      id: 'yakgwa',
      name: '약과',
      description: '꿀과 참기름의 고소한 향이 어우러진 전통 약과',
      price: 15000,
      image: 'https://readdy.ai/api/search-image?query=Traditional%20Korean%20yakgwa%20honey%20cookies%20beautifully%20arranged%20on%20elegant%20white%20ceramic%20plate%2C%20golden%20brown%20glossy%20surface%2C%20detailed%20close-up%20shot%2C%20simple%20cream%20background%2C%20professional%20food%20photography%2C%20appetizing%20presentation%2C%20artisanal%20handmade%20quality&width=400&height=400&seq=yakgwa-related-003&orientation=squarish',
      link: '/product/yakgwa',
      tag: '인기 메뉴',
    },
    {
      id: 'yugwa',
      name: '유과',
      description: '찹쌀을 정성껏 튀겨 만든 바삭하고 고소한 전통 유과',
      price: 18000,
      image: 'https://readdy.ai/api/search-image?query=Traditional%20Korean%20yugwa%20rice%20puff%20snacks%20colorful%20assortment%20beautifully%20arranged%20on%20elegant%20white%20ceramic%20plate%2C%20light%20airy%20texture%2C%20pastel%20colors%2C%20detailed%20close-up%20shot%2C%20simple%20cream%20background%2C%20professional%20food%20photography%2C%20appetizing%20presentation%2C%20artisanal%20handmade%20quality&width=400&height=400&seq=yugwa-related-003&orientation=squarish',
      link: '/product/yugwa',
      tag: '프리미엄',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* 네비게이션 */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-12">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/f77122aa20e12351d50b895437d7646f/26f7e4287336b6a14c4caa48b3ce1ec3.png"
                alt="초이담 참새한과"
                className="h-12 w-auto cursor-pointer"
              />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer"
              >
                홈
              </Link>
              <a
                href="/#about"
                className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer"
              >
                브랜드 소개
              </a>
              <a
                href="/#menu"
                className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer"
              >
                메뉴
              </a>
              <a
                href="/#contact"
                className="text-sm font-medium text-gray-900 hover:text-amber-700 transition-colors cursor-pointer"
              >
                문의
              </a>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CartButton isScrolled={isScrolled} />
            <button className="px-6 py-2.5 bg-gray-900 text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer">
              주문하기
            </button>
          </div>
        </div>
      </nav>

      {/* 상품 상세 섹션 */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            {/* 이미지 영역 */}
            <div className="sticky top-32">
              <div className="bg-amber-50 rounded-3xl overflow-hidden aspect-square">
                <img
                  src="https://readdy.ai/api/search-image?query=Premium%20Korean%20traditional%20confectionery%20gift%20set%20in%20elegant%20wooden%20box%2C%20assorted%20hangwa%20varieties%20including%20yakgwa%20and%20yugwa%20beautifully%20arranged%2C%20luxurious%20presentation%20with%20traditional%20bojagi%20fabric%2C%20simple%20cream%20background%2C%20professional%20product%20photography%2C%20high-end%20gift%20display&width=800&height=800&seq=giftset-detail-001&orientation=squarish"
                  alt="한과 선물 세트"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* 상품 정보 영역 */}
            <div>
              <div className="mb-8">
                <span className="inline-block bg-amber-100 px-4 py-2 rounded-full text-sm text-amber-900 font-medium mb-4">
                  명절 선물 추천
                </span>
                <h1
                  className="text-5xl font-bold text-gray-900 mb-4"
                  style={{ fontFamily: "'Noto Serif KR', serif" }}
                >
                  한과 선물 세트
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  정성스럽게 준비한 약과와 유과를 고급스러운 선물 세트로
                  구성했습니다. 명절이나 특별한 날의 선물로 완벽합니다.
                </p>
              </div>

              {/* 가격 */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <p className="text-4xl font-bold text-gray-900">
                  {selectedPrice.toLocaleString()}원
                </p>
              </div>

              {/* 세트 구성 선택 */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  세트 구성 선택
                </h3>
                <div className="space-y-3">
                  {giftSets.map((set) => (
                    <button
                      key={set.id}
                      onClick={() => setSelectedSet(set.id)}
                      className={`w-full px-6 py-4 rounded-xl text-left transition-all cursor-pointer ${
                        selectedSet === set.id
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-lg">{set.name}</span>
                        <span className="font-bold">
                          {set.price.toLocaleString()}원
                        </span>
                      </div>
                      <p
                        className={`text-sm ${
                          selectedSet === set.id
                            ? 'text-white/80'
                            : 'text-gray-500'
                        }`}
                      >
                        {set.items}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* 수량 선택 */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  수량
                </h3>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => handleQuantityChange('decrease')}
                    className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line text-gray-900 text-xl"></i>
                  </button>
                  <span className="text-2xl font-bold text-gray-900 w-16 text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => handleQuantityChange('increase')}
                    className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-gray-900 text-xl"></i>
                  </button>
                </div>
              </div>

              {/* 총 가격 */}
              <div className="mb-8 p-6 bg-amber-50 rounded-2xl">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-medium text-gray-900">
                    총 금액
                  </span>
                  <span className="text-3xl font-bold text-gray-900">
                    {totalPrice.toLocaleString()}원
                  </span>
                </div>
              </div>

              <button 
                onClick={handleAddToCart}
                className="w-full px-8 py-5 bg-gray-900 text-white rounded-full text-lg font-medium hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
              >
                장바구니 담기
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 주문 폼 섹션 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-8">
          <div className="mb-12">
            <h2
              className="text-4xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Noto Serif KR', serif" }}
            >
              주문 정보 입력
            </h2>
            <p className="text-gray-600">
              선물 세트 주문을 위한 정보를 입력해 주세요
            </p>
          </div>

          <form
            id="gift-set-order-form"
            onSubmit={handleSubmit}
            data-readdy-form
            className="bg-white rounded-2xl p-8 shadow-sm"
          >
            {/* 이름 */}
            <div className="mb-6">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                이름 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="홍길동"
              />
            </div>

            {/* 연락처 */}
            <div className="mb-6">
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                연락처 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="010-1234-5678"
              />
            </div>

            {/* 이메일 */}
            <div className="mb-6">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                이메일 <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="example@email.com"
              />
            </div>

            {/* 배송지 주소 */}
            <div className="mb-6">
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                배송지 주소 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                placeholder="서울시 강남구 테헤란로 123"
              />
            </div>

            {/* 희망 배송일 */}
            <div className="mb-6">
              <label
                htmlFor="deliveryDate"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                희망 배송일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="deliveryDate"
                name="deliveryDate"
                value={formData.deliveryDate}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {/* 선물 메시지 */}
            <div className="mb-6">
              <label
                htmlFor="message"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                선물 메시지 (선택)
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                placeholder="선물과 함께 전달할 메시지를 입력해 주세요 (최대 500자)"
              />
              <p className="text-xs text-gray-500 mt-2">
                {formData.message.length}/500자
              </p>
            </div>

            {/* 선물 카드 추가 */}
            <div className="mb-8">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="giftCard"
                  checked={formData.giftCard}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-amber-600 bg-gray-50 border-gray-300 rounded focus:ring-2 focus:ring-amber-500 cursor-pointer"
                />
                <span className="text-sm text-gray-900">
                  선물 카드 추가 (무료)
                </span>
              </label>
            </div>

            {/* 주문 요약 */}
            <div className="mb-8 p-6 bg-amber-50 rounded-xl">
              <h3 className="font-bold text-gray-900 mb-4">주문 요약</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">선택 세트</span>
                  <span className="font-medium text-gray-900">
                    {giftSets.find((s) => s.id === selectedSet)?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">구성</span>
                  <span className="font-medium text-gray-900">
                    {giftSets.find((s) => s.id === selectedSet)?.items}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">수량</span>
                  <span className="font-medium text-gray-900">{quantity}개</span>
                </div>
                <div className="pt-3 mt-3 border-t border-amber-200 flex justify-between">
                  <span className="font-bold text-gray-900">총 금액</span>
                  <span className="font-bold text-gray-900 text-lg">
                    {totalPrice.toLocaleString()}원
                  </span>
                </div>
              </div>
            </div>

            {/* 제출 상태 메시지 */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-800">
                  주문이 성공적으로 접수되었습니다. 곧 연락드리겠습니다!
                </p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-800">
                  주문 접수 중 오류가 발생했습니다. 다시 시도해 주세요.
                </p>
              </div>
            )}

            {/* 제출 버튼 */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-5 bg-gray-900 text-white rounded-full text-lg font-medium hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? '주문 접수 중...' : '주문하기'}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              주문 접수 후 담당자가 확인하여 연락드립니다
            </p>
          </form>
        </div>
      </section>

      {/* 상세 정보 섹션 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* 원재료 */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-plant-line text-amber-900 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">원재료</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• 약과: 밀가루, 꿀, 참기름 (국내산)</li>
                <li>• 유과: 찹쌀, 쌀조청 (국내산)</li>
                <li>• 생강즙, 계피가루</li>
                <li>• 천연 색소</li>
                <li>• 소금</li>
              </ul>
            </div>

            {/* 보관방법 */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-fridge-line text-amber-900 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">보관방법</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                직사광선과 습기를 피해 서늘하고 건조한 곳에 보관해 주세요.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                개봉 후에는 밀폐 용기에 담아 냉장 보관하시면 더 오래 신선하게
                드실 수 있습니다.
              </p>
              <p className="text-sm text-amber-900 font-medium">
                유통기한: 제조일로부터 30-45일
              </p>
            </div>

            {/* 특징 */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-star-line text-amber-900 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">특징</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-amber-900 mt-1"></i>
                  <span>주문 제작으로 신선함 보장</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-amber-900 mt-1"></i>
                  <span>100% 국내산 재료 사용</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-amber-900 mt-1"></i>
                  <span>고급스러운 선물 포장</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-amber-900 mt-1"></i>
                  <span>무료 선물 카드 제공</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 관련 상품 추천 섹션 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-12">
            <h2
              className="text-4xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Noto Serif KR', serif" }}
            >
              개별 상품도 만나보세요
            </h2>
            <p className="text-gray-600">선물 세트에 포함된 한과를 개별로도 구매하실 수 있습니다</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {relatedProducts.map((product) => (
              <Link
                key={product.id}
                to={product.link}
                className="group bg-white rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block bg-amber-100 px-3 py-1 rounded-full text-xs text-amber-900 font-medium mb-3">
                    {product.tag}
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-amber-700 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  <p className="text-xl font-bold text-gray-900">
                    {product.price.toLocaleString()}원~
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 푸터 */}
      <footer className="bg-gradient-to-b from-amber-900 to-amber-950 text-white mx-5 mb-5 rounded-t-3xl">
        <div className="max-w-7xl mx-auto px-12 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-1">
              <h3
                className="text-3xl font-bold leading-tight mb-6"
                style={{ fontFamily: "'Noto Serif KR', serif" }}
              >
                초이담과 함께<br />
                특별한 선물을
              </h3>
            </div>

            <div>
              <h4 className="font-medium text-white mb-6">메뉴</h4>
              <ul className="space-y-4 text-amber-100">
                <li>
                  <Link
                    to="/"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    홈
                  </Link>
                </li>
                <li>
                  <a
                    href="/#about"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    브랜드 소개
                  </a>
                </li>
                <li>
                  <a
                    href="/#menu"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    메뉴
                  </a>
                </li>
                <li>
                  <a
                    href="/#contact"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    문의
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-white mb-6">소셜 미디어</h4>
              <ul className="space-y-4 text-amber-100">
                <li className="flex items-center gap-3 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-lg"></i>
                  <span>Instagram</span>
                </li>
                <li className="flex items-center gap-3 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-article-line text-lg"></i>
                  <span>Blog</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-white mb-3">문의</h4>
              <p className="text-sm text-amber-100 mb-2">
                전화: 02-1234-5678
              </p>
              <p className="text-sm text-amber-100">
                이메일: info@choidam.com
              </p>
            </div>
          </div>

          <div className="relative pt-8 border-t border-amber-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-amber-200">
                © 2024 초이담 참새한과. All rights reserved.
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-amber-200 hover:text-white transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      <AddToCartToast 
        isVisible={showToast} 
        onClose={() => setShowToast(false)} 
        productName="한과 선물 세트" 
      />
    </div>
  );
}
